/* eslint-disable react/no-unknown-property */
import { createContext, useContext } from 'react';
import { Link } from '@tanstack/react-router';

import config from '@/config';
import { Issue, Article } from '@/constants';

import styles from './SingleIssue.module.css';
import { HomeContext } from './Home.context';

export function SingleIssue({ issue }: { issue: Issue }) {
	const { selectedSection, hasUserAccess, userFeatures } = useContext(
		HomeContext,
	);
	const id = issue.slug ? issue.slug : issue.id;
	const { articles } = issue;
	const from = `${window.location.pathname}?section=${selectedSection}`;
	const hasAccess = hasUserAccess(userFeatures, issue);
	return (
		<SingleIssueContext.Provider
			value={{
				issueId: id,
				articles: articles ? articles : [],
				issue,
				from,
				hasAccess,
			}}
		>
			<div className={[styles['single']].join(' ')}>
				<div className={styles['cover']}>
					<Cover />
					<Toc />
				</div>
			</div>
		</SingleIssueContext.Provider>
	);
}

const SingleIssueContext = createContext<SingleIssueContextProps>({
	issue: {
		id: '',
		name: '',
		description: '',
		index: 0,
		cover: '',
		features: [],
		isFree: false,
		pages: [],
		slug: '',
		externalUrl: '',
	},
	from: '',
	issueId: '',
	hasAccess: false,
	articles: [],
});

interface SingleIssueContextProps {
	issue: Issue;
	issueId: string;
	from: string;
	hasAccess: boolean;
	articles: Array<Article>;
}

function Cover() {
	const { issue, from, hasAccess } = useContext(SingleIssueContext);
	const { onClickDisabledIssue } = useContext(HomeContext);
	const { name, cover, slug, id } = issue;
	const { description } = issue;

	const contentProtection = config?.app?.contentProtection;
	const imageComponent = hasAccess ? (
		<Link
			to={`/issues/$id`}
			from={from}
			params={{
				id: slug ?? id,
			}}
			className={styles['image']}
		>
			<figure role="button" style={{ cursor: 'pointer' }}>
				{contentProtection ? (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={500}
						height={667}
						className="content-protection"
						onContextMenu={e => e.preventDefault()}
					/>
				) : (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={500}
						height={667}
					/>
				)}
			</figure>
		</Link>
	) : (
		// eslint-disable-next-line jsx-a11y/anchor-is-valid
		<a
			href="#"
			className={styles['disabled']}
			onClick={() => {
				onClickDisabledIssue(issue);
			}}
		>
			<figure role="button" style={{ cursor: 'pointer' }}>
				{contentProtection ? (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={500}
						height={667}
						className="content-protection"
						onContextMenu={e => e.preventDefault()}
					/>
				) : (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={500}
						height={667}
					/>
				)}
			</figure>
		</a>
	);
	return (
		<section>
			{imageComponent}
			<div className={styles['title']}>
				<h1>{name}</h1>
				{description ? <p>{description}</p> : null}
				<hr />
			</div>
		</section>
	);
}

function Toc() {
	const { articles } = useContext(SingleIssueContext);
	const filteredArticles = articles.filter(onlyIncludedToc).sort(sortByIndex);
	return (
		<section>
			<ul className={styles['toc']}>
				{filteredArticles.map((article: Article) => (
					<TocItem key={article.id} article={article} />
				))}
			</ul>
		</section>
	);
}

function TocItem({ article }: TocItemProps) {
	const { issueId, from, hasAccess, issue } = useContext(SingleIssueContext);
	const { onClickDisabledIssue } = useContext(HomeContext);
	return (
		<li
			key={article.id}
			article-index={article.index}
			is-feature={article.isFeatured ? `` : undefined}
		>
			{hasAccess ? (
				<Link
					to={`/issues/$id/$articleId`}
					params={{
						id: issueId,
						articleId: article.slug ?? article.id,
					}}
					from={from}
				>
					{article.name['en']}
				</Link>
			) : (
				// eslint-disable-next-line jsx-a11y/anchor-is-valid
				<a
					href="#"
					onClick={() => {
						onClickDisabledIssue(issue);
					}}
				>
					{article.name['en']}
				</a>
			)}
		</li>
	);
}

interface TocItemProps {
	article: Article;
}

function sortByIndex(a: Article, b: Article): number {
	return a.index - b.index;
}

function onlyIncludedToc(article: any) {
	if (article.role === 'advert') {
		return false;
	}
	return (
		article.channel === undefined ||
		article.channel === null ||
		article?.channel?.toc === 'included'
	);
}
